import React, { useEffect, useState } from 'react'
import { BottomSheet, Button, MessageBox, Typography } from '@s4l/ds'

import styles from './index.module.css'
import { IconWarning } from '@s4l/icons/IconWarning'
import { useBrowser } from '@s4l/utils/hooks/useBrowser'
import { useS4LContext } from '@s4l/react-context'

interface IDeleteAccountProps {
  onDelete: () => void | Promise<void>
  disabled: boolean
}

function DeleteAccount(props: Readonly<IDeleteAccountProps>) {
  const { onDelete, disabled } = props
  const { i18n } = useS4LContext()
  const [onClient, setOnClient] = useState(false)

  const { isIOSApp } = useBrowser()

  const [open, setOpen] = React.useState(false)

  const handleDelete = async () => {
    await onDelete()
    setOpen(false)
  }

  useEffect(() => {
    setOnClient(true)
  }, [])

  /**
   * This component only renders in the iOS app to comply with Apple's App Store requirements
   * for account deletion functionality
   */
  if (!isIOSApp) return null

  if (!onClient) return null

  return (
    <div className={styles.deleteAccountContainer}>
      <Typography as="h5" variant="title" size="xs" bold>
        {i18n.formatMessage({ id: 'delete-user.title' })}
      </Typography>
      {disabled ? (
        <MessageBox kind="info">
          <MessageBox.Title>
            {i18n.formatMessage({ id: 'delete-user.notification' })}
          </MessageBox.Title>
        </MessageBox>
      ) : (
        <Typography as="p" variant="body" size="l">
          {i18n.formatMessage({ id: 'delete-user.body' })}
        </Typography>
      )}
      <BottomSheet open={open} onOpenChange={setOpen}>
        <BottomSheet.Trigger asChild>
          <Button
            disabled={disabled}
            variant="text-flat"
            size="large"
            customColor="var(--color-error-500)"
          >
            {i18n.formatMessage({ id: 'delete-user.cta' })}
          </Button>
        </BottomSheet.Trigger>
        <BottomSheet.Content title={i18n.formatMessage({ id: 'delete-user.title' })}>
          <div className={styles.bottomSheetContent}>
            <IconWarning />
            <Typography as="h5" variant="title" size="xs" bold>
              {i18n.formatMessage({ id: 'delete-user.modal-title' })}
            </Typography>
            <Typography as="p" variant="body" size="l">
              {i18n.formatMessage({ id: 'delete-user.modal-body' })}
            </Typography>
            <div className={styles.buttonContainer}>
              <Button
                fullWidth
                variant="outlined-rounded"
                size="large"
                onClick={() => setOpen(false)}
              >
                {i18n.formatMessage({ id: 'global.back' })}
              </Button>
              <Button
                fullWidth
                variant="filled-rounded"
                size="large"
                onClick={handleDelete}
                customColor="var(--color-error-500)"
              >
                {i18n.formatMessage({ id: 'delete-user.cta' })}
              </Button>
            </div>
          </div>
        </BottomSheet.Content>
      </BottomSheet>
    </div>
  )
}

DeleteAccount.displayName = 'DeleteAccount'

export default DeleteAccount
